import { fetchListing, fetchStream, proxyUrl, type ProviderSummary, type VideoSummary } from "./api.ts";
import { resolveImageUrl } from "./components.ts";
import { el } from "./dom.ts";
import { feedProviders } from "./store.ts";
import { openVideo, type AppContext } from "./views.ts";
import { visibleProviders } from "./visibility.ts";

type Mode = "grid" | "shorts";
const MODE_KEY = "aiox:feed-mode";

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "shorts" ? "shorts" : "grid";
  } catch {
    return "grid";
  }
}

function writeMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* a private window still gets a feed */
  }
}

/**
 * The feed is pictures, not rows: a masonry wall by default, and a one-at-a-time
 * vertical reel when Shorts is on. Tapping a tile starts the video in the player
 * instead of opening a page first — the feed is for watching, not for reading.
 */
export function feedMasonryView(context: AppContext): HTMLElement {
  const root = el("div", { class: "view view--wall" });
  const wall = el("div", { class: "wall", attrs: { "aria-label": "Titles across every site" } });
  const reels = el("div", { class: "reels", attrs: { hidden: true } });
  const sentinel = el("div", { class: "wall-sentinel", attrs: { "aria-hidden": "true" } });
  const status = el("p", { class: "wall-status", attrs: { role: "status", "aria-live": "polite" } });

  const working = visibleProviders(context.providers).filter(
    (provider) => provider.state !== "offline",
  );
  const pinned = feedProviders()
    .map((id) => working.find((provider) => provider.id === id))
    .filter((provider): provider is ProviderSummary => Boolean(provider));

  /** Shuffle on every mount: a refresh should not replay the same wall. */
  const shuffled = <T,>(items: T[]): T[] => {
    const copy = [...items];
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[swap]] = [copy[swap], copy[index]];
    }
    return copy;
  };

  const pool = [...pinned, ...working.filter((provider) => !pinned.includes(provider))];
  const mixed = shuffled(pool);

  const seen = new Set<string>();
  const collected: VideoSummary[] = [];
  const names = new Map(working.map((provider) => [provider.id, provider.name]));
  let cursor = 0;
  let page = 1;
  let loading = false;
  let done = false;
  let mode: Mode = readMode();
  const WAVE = 10;

  const play = (item: VideoSummary): void => {
    openVideo(context, item);
  };

  const tile = (item: VideoSummary, providerName: string): HTMLElement | null => {
    const source = item.image ?? item.thumbnail;
    if (!source) return null;
    const link = el("a", {
      class: "wall-tile",
      attrs: {
        href: `#/watch/${encodeURIComponent(item.provider)}/${encodeURIComponent(item.id)}`,
        "aria-label": `Play ${item.title || "this title"} from ${providerName}`,
        "data-provider": item.provider,
        "data-id": item.id,
      },
    });
    const image = el("img", {
      class: "wall-image",
      attrs: { alt: "", loading: "lazy", decoding: "async", referrerpolicy: "no-referrer" },
    }) as HTMLImageElement;
    image.addEventListener("load", () => {
      link.dataset.shape = image.naturalHeight > image.naturalWidth * 1.02 ? "tall" : "wide";
    });
    image.addEventListener("error", () => {
      const fallback = item.thumbnail;
      if (fallback && image.src !== fallback) {
        void resolveImageUrl(fallback).then((resolved) => {
          image.src = resolved;
        });
        return;
      }
      link.dataset.broken = "true";
      image.remove();
    });
    void resolveImageUrl(source).then((resolved) => {
      image.src = resolved;
    });
    link.append(image);
    link.addEventListener("click", (event) => {
      event.preventDefault();
      play(item);
    });
    return link;
  };

  const reel = (item: VideoSummary, providerName: string): HTMLElement => {
    const holder = el("article", {
      class: "reel",
      attrs: { "data-provider": item.provider, "data-id": item.id },
    });
    const poster = item.image ?? item.thumbnail;
    if (poster) {
      void resolveImageUrl(poster).then((resolved) => {
        holder.style.setProperty("--reel-poster", `url("${resolved}")`);
      });
    }
    const button = el("button", {
      class: "reel-open",
      attrs: { type: "button", "aria-label": `Play ${item.title || "this title"} from ${providerName}` },
    });
    button.addEventListener("click", () => play(item));
    holder.append(button);
    return holder;
  };

  /** The reel that holds the most of the viewport gets the actual stream. */
  const attachStream = async (holder: HTMLElement): Promise<void> => {
    if (holder.dataset.loading === "true" || holder.querySelector("video")) return;
    holder.dataset.loading = "true";
    try {
      const media = await fetchStream(holder.dataset.provider ?? "", holder.dataset.id ?? "");
      const ref = media.find((entry) => entry.type === "mp4" && (entry.playUrl || entry.url)) ?? media[0];
      if (!ref) return;
      const video = el("video", {
        class: "reel-video",
        attrs: { muted: "", loop: "", playsinline: "", preload: "metadata", "aria-hidden": "true", tabindex: "-1" },
      }) as HTMLVideoElement;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.src = proxyUrl(ref);
      // The poster stays until a frame is actually moving; a video that has not
      // decoded yet paints black and looks broken.
      video.addEventListener(
        "playing",
        () => {
          holder.dataset.playing = "true";
        },
        { once: true },
      );
      holder.prepend(video);
      await video.play().catch(() => undefined);
    } catch {
      /* the poster stays */
    } finally {
      holder.dataset.loading = "false";
    }
  };

  const detachStream = (holder: HTMLElement): void => {
    const video = holder.querySelector("video");
    if (!video) return;
    video.pause();
    video.removeAttribute("src");
    video.load();
    video.remove();
    delete holder.dataset.playing;
  };

  const paint = (): void => {
    wall.hidden = mode !== "grid";
    reels.hidden = mode !== "shorts";
  };

  const shapeOf = (item: VideoSummary): Promise<"tall" | "wide"> => {
    const source = item.image ?? item.thumbnail;
    if (!source) return Promise.resolve("wide");
    return resolveImageUrl(source).then(
      (resolved) =>
        new Promise<"tall" | "wide">((resolve) => {
          const probe = new Image();
          probe.onload = () =>
            resolve(probe.naturalHeight > probe.naturalWidth * 1.02 ? "tall" : "wide");
          probe.onerror = () => resolve("wide");
          probe.src = resolved;
        }),
    );
  };

  const renderShorts = async (): Promise<void> => {
    reels.replaceChildren();
    // The wall is hidden in this mode, so its lazy images never load and cannot
    // tell us a shape. Measure the first forty directly, then keep the portrait
    // ones — a reel of landscape clips is not the thing that was asked for.
    const sample = collected.slice(0, 40);
    const shapes = await Promise.all(sample.map(shapeOf));
    const portrait = sample.filter((_, index) => shapes[index] === "tall");
    const source = portrait.length >= 4 ? portrait : collected;
    for (const item of source) {
      reels.append(reel(item, names.get(item.provider) ?? item.provider));
    }
    // One reel plays: whichever sits nearest the middle of the viewport. A
    // scroll handler is used rather than an observer because the container is
    // itself the scroller and ratio events there are easy to miss.
    syncActive();
  };

  let syncTimer = 0;
  const syncActive = (): void => {
    const box = reels.getBoundingClientRect();
    if (box.height === 0) return;
    const middle = box.top + box.height / 2;
    let best: HTMLElement | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const node of reels.querySelectorAll<HTMLElement>(".reel")) {
      const rect = node.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = node;
      }
    }
    for (const node of reels.querySelectorAll<HTMLElement>(".reel")) {
      if (node !== best) detachStream(node);
    }
    if (best) void attachStream(best);
  };

  reels.addEventListener(
    "scroll",
    () => {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(syncActive, 120);
    },
    { passive: true },
  );

  const loadWave = async (): Promise<void> => {
    if (loading || done) return;
    loading = true;
    status.textContent = "Loading more…";
    try {
      const batch = mixed.slice(cursor, cursor + WAVE);
      cursor += batch.length;
      if (batch.length === 0) {
        if (page >= 3) {
          done = true;
          status.textContent = "That is everything for now.";
          return;
        }
        page += 1;
        cursor = 0;
        return;
      }
      const answers = await Promise.all(
        batch.map(async (provider) => {
          try {
            return { provider, items: await fetchListing(provider.id, { page, sort: "popular", limit: 12 }) };
          } catch {
            return { provider, items: [] };
          }
        }),
      );
      const fragment = document.createDocumentFragment();
      for (const { provider, items } of answers) {
        for (const item of shuffled(items)) {
          const key = `${item.provider}:${item.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          collected.push(item);
          const node = tile(item, provider.name);
          if (node) fragment.append(node);
        }
      }
      wall.append(fragment);
      if (mode === "shorts") void renderShorts();
      status.textContent = "";
    } finally {
      loading = false;
      if (collected.length < 12) void loadWave();
    }
  };

  if ("IntersectionObserver" in window) {
    new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadWave();
      },
      { rootMargin: "900px 0px" },
    ).observe(sentinel);
  }

  const modeGroup = el("div", {
    class: "tag-row feed-mode",
    attrs: { role: "group", "aria-label": "Feed layout" },
  });
  const modeButtons: Record<Mode, HTMLButtonElement> = {
    grid: el("button", { class: "tag", attrs: { type: "button", "aria-pressed": "true" }, text: "Grid" }),
    shorts: el("button", { class: "tag", attrs: { type: "button", "aria-pressed": "false" }, text: "Shorts" }),
  };
  for (const [key, button] of Object.entries(modeButtons) as [Mode, HTMLButtonElement][]) {
    button.addEventListener("click", () => {
      mode = key;
      writeMode(key);
      modeButtons.grid.setAttribute("aria-pressed", String(key === "grid"));
      modeButtons.shorts.setAttribute("aria-pressed", String(key === "shorts"));
      paint();
      if (key === "shorts") {
        void renderShorts();
        reels.scrollIntoView({ block: "start" });
      } else {
        for (const node of reels.querySelectorAll(".reel")) detachStream(node as HTMLElement);
      }
      status.textContent = key === "shorts" ? "Vertical titles, one at a time." : "";
    });
  }
  modeGroup.append(modeButtons.grid, modeButtons.shorts);
  modeButtons.shorts.setAttribute("aria-pressed", String(mode === "shorts"));
  modeButtons.grid.setAttribute("aria-pressed", String(mode === "grid"));

  root.append(
    el(
      "header",
      { class: "page-head container wall-head" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Feed" }),
        el("p", { class: "page-sub", text: "Everything, in pictures. Tap one and it plays." }),
      ),
      el(
        "div",
        { class: "wall-tools" },
        modeGroup,
        (() => {
          const customize = el("button", {
            class: "btn btn-ghost btn-sm",
            attrs: { type: "button" },
            text: "Customize feed",
          });
          customize.addEventListener("click", () => context.navigate("#/customize"));
          return customize;
        })(),
      ),
    ),
    el("div", { class: "container" }, wall, reels),
    el("div", { class: "container" }, status, sentinel),
  );

  paint();
  void loadWave();
  return root;
}
